/**
 * Campaign journals — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const campaignJournalsPack: ToilePack = {
    slug: 'campaign-journals',
    category: 'tabletop',
    name: 'Campaign journals',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'log', label: 'Session log', subject: 'a journal page with labelled lines "Session" and "Date" at the top, a large ruled area for notes, and a small box marked "XP" in the corner'},
        {key: 'people', label: 'Characters met', subject: 'a journal page titled "Characters Met" with six ruled entries, each with an empty small portrait circle and lines for name and notes'},
        {key: 'quests', label: 'Quest tracker', subject: 'a journal page titled "Quests" with a ruled table of rows with a tick box, a quest line and a "Reward" column'},
    ],
    styles: [
        {key: 'parchment', label: 'Parchment', style: 'an aged parchment journal page with inked rules and a decorative corner flourish', palette: 'sepia ink on warm tan paper', framing: 'the page filled inside a wide margin'},
        {key: 'leather', label: 'Leather book', style: "a leather-bound adventurer's book page with a stitched edge and gilded rules", palette: 'gold ink on dark brown', framing: 'the page filled inside a wide margin'},
        {key: 'clean', label: 'Clean notes', style: 'a clean modern notes page with thin gray rules and a small header', palette: 'black ink with gray rules on white', framing: 'the page filled inside a wide margin'},
    ],
    directions: ['pirate campaign', 'horror', 'sci-fi'],
    directionHint: 'try: pirate campaign',
    seo: {
        title: 'Campaign journals: session log, characters, quests',
        metaDescription: 'Printable campaign journal pages in three styles: a session log, a characters-met page and a quest tracker.',
        h1: 'Campaign journals: session log, characters met, quest tracker',
        tagline: 'Nine pages: a session log, a characters-met page and a quest tracker, in three styles.',
        intro: 'A player or a game master keeps a journal through a long campaign: sessions, people met and places seen. This pack shows a session log with a large notes area, a characters-met page with six entries, and a quest tracker with tick boxes. Each comes as an aged parchment page, a leather-bound book page or a clean notes page. Type a campaign, such as a pirate campaign, and the pages keep their space.',
        useCases: [
            'a log for each session of a long campaign',
            'a record of the people met on the road',
            'a quest list to tick off as they are done',
            "a notes page for a player's own journal",
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: a session log, a characters-met page and a quest tracker.'},
            {q: 'How many entries are on the characters page?', a: 'Six, each with a small portrait circle and lines for a name and notes.'},
            {q: 'Is there room for notes on the session log?', a: 'Yes. A large ruled area takes the notes, with a Session line, a Date line and an XP box in the corner.'},
            {q: 'What is the difference between the styles?', a: 'Parchment is sepia ink on tan paper. Leather book is gold ink on dark brown with a stitched edge. Clean notes is black ink with grey rules on white.'},
        ],
    },
}
